/* global React */

// ─────────────────────────────────────────────────────────────
// TOSY app home — brand strip, rotating banner, 8-tile bento,
// hours strip, fixed glass quickbar. All real photography.
// ─────────────────────────────────────────────────────────────

const BRAND = { pink: '#ff2e88', blue: '#2e9bff', yellow: '#ffd23f', ink: '#070d1c', bone: '#eaf2ff' };

function RotatingBanner({ accent }) {
  const E = window.TOSY_EVENT;
  const PRERENDER = !!window.__TOSY_PRERENDER__;
  const items = [
    // The event line and the "rooms free tonight" line both go stale on a fixed
    // date/time, so neither is prerendered — see the PRERENDER note above
    // getHours(). The rest of this list is true whenever the bar is open.
    ...(E && E.upcoming && !PRERENDER ? [{ k: 'This Sat', v: `${E.name} — ${E.price}` }] : []),
    { k: 'Tonight',   v: 'Happy Hour til 7pm' },
    { k: 'Nearby',    v: '5 min from London Bridge' },
    { k: 'Walk In',   v: 'No booking needed at the bar' },
    { k: 'On now',    v: 'Cocktails 2 for £10' },
    { k: 'Terrace',   v: 'Heated · open year-round' },
    ...(PRERENDER ? [] : [{ k: 'Available', v: 'Karaoke rooms free tonight' }]),
  ];
  const [i, setI] = React.useState(0);
  const [fade, setFade] = React.useState(true);
  React.useEffect(() => {
    const t = setInterval(() => {
      setFade(false);
      setTimeout(() => { setI(x => (x + 1) % items.length); setFade(true); }, 400);
    }, 3800);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{
      padding: '9px 14px', borderRadius: 100,
      border: `1px solid ${accent}44`, background: `${accent}14`,
      display: 'flex', alignItems: 'center', gap: 9, overflow: 'hidden',
      transition: 'opacity 0.4s ease', opacity: fade ? 1 : 0,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: accent, boxShadow: `0 0 10px ${accent}`, flexShrink: 0 }} />
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, letterSpacing: '0.22em', color: accent, textTransform: 'uppercase', flexShrink: 0 }}>{items[i].k}</span>
      <span style={{ fontFamily: 'var(--sans)', fontSize: 13, color: 'var(--bone)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{items[i].v}</span>
    </div>
  );
}

// PRERENDER — prerender.mjs renders this app to static HTML at build time and
// bakes it into index.html. That HTML is then served to everyone until the next
// deploy, so anything that depends on the current time or date MUST be left out
// of it and filled in by React on mount. window.__TOSY_PRERENDER__ is true only
// during that build-time render. Currently excluded: the OPEN/CLOSED badge, the
// hours line, TonightStrip and EventStrip. Nothing here calls the booking API,
// so no price can leak into the static HTML — keep it that way.
//
// VENUE hours — the latest you can WALK IN. Confirmed by the client 11 Aug 2026.
//   Tue/Wed  11pm — but a full-venue booking (e.g. Gaymers) can push it to 12
//   Thu      12am
//   Fri/Sat  last orders 12:30, everyone out by 1am
//
// These are deliberately NOT the karaoke session windows, which come from the
// booking engine and can finish later — a Thursday room can run to 12:15am even
// though the door shuts at 12. That is intended, not a bug: the cutoff exists so
// nobody wanders in for a drink at 12:15, while a booked room plays out its
// session. Don't "fix" the two to match.
// Keep in step with the openingHoursSpecification and the <noscript> block in
// index.html — all three describe the same thing and have drifted apart before.
// Sunday-first, matching Date#getDay(). `from`/`to` are minutes past that day's
// midnight; `to` deliberately exceeds 1440 where we run past it, so Fri/Sat
// close at 1am the FOLLOWING day.
const SCHEDULE = [
  null,                                                                            // Sun
  null,                                                                            // Mon
  { from: 17 * 60 + 30, to: 23 * 60,      label: '5:30pm – 11pm' },                 // Tue
  { from: 17 * 60 + 30, to: 23 * 60,      label: '5:30pm – 11pm' },                 // Wed
  { from: 17 * 60 + 30, to: 24 * 60,      label: '5:30pm – 12am' },                 // Thu
  { from: 17 * 60 + 30, to: 24 * 60 + 60, label: '5:30pm – 1am', note: 'Last orders 12:30' },  // Fri
  { from: 17 * 60 + 30, to: 24 * 60 + 60, label: '5:30pm – 1am', note: 'Last orders 12:30' },  // Sat
];
const SHORT_DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hoursTail = t => t.note ? `${t.note} · Happy Hour til 7` : 'Happy Hour til 7 · Bar walk-ins welcome';

// `open` is whether we are open RIGHT NOW. It used to be the truthiness of the
// hours string, which meant every Tue–Sat read OPEN around the clock — the badge
// said OPEN at 11:32 on a Wednesday morning. It must be compared against the time.
function getHours() {
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  const day = now.getDay();

  // Still inside last night's session? Fri/Sat run to 1am, so at 00:30 on a
  // Saturday we are open, and it's Friday's entry that describes it.
  const yDay = (day + 6) % 7;
  const y = SCHEDULE[yDay];
  if (y && y.to > 24 * 60 && mins < y.to - 24 * 60) {
    return { label: `${SHORT_DAY[yDay]} ${y.label} · ${hoursTail(y)}`, open: true };
  }

  const t = SCHEDULE[day];
  if (!t) return { label: `${SHORT_DAY[day]} — Closed · Open Tue–Sat`, open: false };
  return { label: `${SHORT_DAY[day]} ${t.label} · ${hoursTail(t)}`, open: mins >= t.from && mins < t.to };
}

function Waveform({ color }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const w = cv.clientWidth, h = cv.clientHeight;
    cv.width = w * dpr; cv.height = h * dpr; ctx.scale(dpr, dpr);
    let raf, t = 0; const bars = 26;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < bars; i++) {
        const n = Math.sin(t * 0.06 + i * 0.5) * 0.4 + Math.sin(t * 0.09 + i * 0.3) * 0.3 + Math.sin(t * 0.04 + i * 1.1) * 0.3;
        const bh = Math.max(2, (0.5 + n * 0.5) * h * 0.9);
        ctx.fillStyle = color; ctx.globalAlpha = 0.85;
        ctx.fillRect(i * (w / bars), (h - bh) / 2, (w / bars) - 2, bh);
      }
      t++; raf = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(raf);
  }, [color]);
  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}


// Tap-to-play music behind the karaoke waveform. First tap starts a random
// track from TOSY_TRACKS; when one ends another random one follows; tap
// again to stop. Inert (plain animation) while the track list is empty.
function MusicWaveform({ color }) {
  const [playing, setPlaying] = React.useState(false);
  const audioRef = React.useRef(null);
  const tracks = window.TOSY_TRACKS || [];

  const playRandom = React.useCallback(() => {
    const a = audioRef.current;
    a.src = tracks[Math.floor(Math.random() * tracks.length)];
    a.play().catch(() => setPlaying(false));
  }, [tracks]);

  const toggle = e => {
    if (!tracks.length) return;
    e.preventDefault(); e.stopPropagation();   // don't open the karaoke sheet
    if (playing) { audioRef.current.pause(); setPlaying(false); return; }
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.addEventListener('ended', playRandom);
    }
    playRandom();
    setPlaying(true);
  };

  React.useEffect(() => () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } }, []);

  return (
    <div onClick={toggle} style={{ height: 26, width: 64, flexShrink: 0, opacity: playing ? 1 : 0.9, position: 'relative' }}>
      <Waveform color={color} />
      {tracks.length > 0 && (
        <span style={{ position: 'absolute', top: -7, right: -7, fontSize: 9 }}>{playing ? '🔊' : '🔇'}</span>
      )}
    </div>
  );
}

// Auto-hides when there's no upcoming event
function EventStrip({ onOpen, style = {} }) {
  const E = window.TOSY_EVENT;
  // Not prerendered: the static HTML is built once and served until the next
  // deploy, so a baked-in event tile would still be advertising the gig days
  // after it happened. React fills this in on mount. See PRERENDER in bento-v2.
  if (window.__TOSY_PRERENDER__) return null;
  if (!E || !E.upcoming) return null;
  return (
    <button onClick={onOpen} style={{
      width: '100%', padding: 0, cursor: 'pointer', ...style,
      border: '1px solid rgba(212,168,74,0.5)', borderRadius: 14, overflow: 'hidden',
      background: '#f2e8d0', display: 'flex', alignItems: 'stretch', textAlign: 'left',
      height: 78,
    }}>
      <img src={E.square} alt={E.name + ' — ' + E.dateLabel} style={{ width: 78, height: 78, objectFit: 'cover', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0, padding: '9px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 2 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 7.5, letterSpacing: '0.22em', color: '#1f6b52' }}>THIS SATURDAY · EVENT</div>
        <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 19, lineHeight: 1, letterSpacing: '-0.03em', color: '#b4232a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{E.name}</div>
        <div style={{ fontFamily: 'var(--sans)', fontSize: 10.5, color: '#3a3128', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{E.dateLabel} · {E.price}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', paddingRight: 12, flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.16em', color: '#fff', background: '#b4232a', padding: '5px 9px', borderRadius: 100, fontWeight: 700 }}>TICKETS</span>
      </div>
    </button>
  );
}

// Late-night CTA — people ring after dark wanting a room and nobody can
// answer the phone. From 5pm until close on open days, push them straight
// to online booking (rooms + studio spots) instead.
function TonightStrip({ onOpen }) {
  const now = new Date();
  const h = now.getHours();
  const day = now.getDay();
  const yesterday = (day + 6) % 7;
  const openDay = d => d >= 2 && d <= 6;
  // Never prerendered — see PRERENDER note above getHours(). Frozen into static
  // HTML this would invite people to sing tonight at 11am on a Monday.
  const show = !window.__TOSY_PRERENDER__ &&
    ((openDay(day) && h >= 17) || (h < 1 && openDay(yesterday)));
  if (!show) return null;
  return (
    <button onClick={onOpen} style={{
      width: '100%', marginBottom: 6, padding: '12px 14px', cursor: 'pointer',
      border: '1px solid rgba(255,46,136,0.55)', borderRadius: 14,
      background: 'linear-gradient(120deg, rgba(255,46,136,0.22), rgba(46,155,255,0.14))',
      display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left', color: 'var(--bone)',
    }}>
      <span style={{ fontSize: 20, flexShrink: 0 }}>🎤</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontFamily: 'var(--display)', fontWeight: 800, fontSize: 15, letterSpacing: '-0.02em', lineHeight: 1.1 }}>Want to sing tonight?</span>
        <span style={{ display: 'block', fontFamily: 'var(--sans)', fontSize: 11, color: 'rgba(234,242,255,0.75)', marginTop: 2 }}>Rooms &amp; studio spots — book online now, no phone call needed</span>
      </span>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.16em', color: '#fff', background: '#ff2e88', padding: '6px 10px', borderRadius: 100, fontWeight: 700, flexShrink: 0 }}>BOOK NOW</span>
    </button>
  );
}

const Label = ({ c, children }) => <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.26em', color: c }}>{children}</div>;
const Title = ({ size = 19, children }) => <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: size, lineHeight: 0.98, letterSpacing: '-0.02em', color: 'var(--bone)' }}>{children}</div>;
const Meta = ({ children }) => <div style={{ fontFamily: 'var(--mono)', fontSize: 8, letterSpacing: '0.14em', color: 'rgba(234,242,255,0.65)', marginTop: 4 }}>{children}</div>;

function BentoViewV2({ onOpenBooking, onOpenMenu, onOpenKaraoke, onOpenClass, onOpenOffers, onOpenTerrace, onOpenFind, onOpenFAQ, onOpenEvent, accentHue }) {
  const P = BRAND;
  const hours = getHours();
  const PRERENDER = !!window.__TOSY_PRERENDER__;
  const tile = {
    borderRadius: 14, border: '1px solid rgba(255,255,255,0.09)', overflow: 'hidden',
    padding: 0, cursor: 'pointer', textAlign: 'left', color: 'var(--bone)', position: 'relative',
    background: 'rgba(255,255,255,0.03)',
  };

  return (
    <main style={{ background: P.ink, minHeight: '100%', color: P.bone, padding: '54px 10px 104px', position: 'relative' }}>
      <style>{`
        @keyframes kb-in { from { transform: scale(1); } to { transform: scale(1.16) translate(-2%,-2%); } }
        @keyframes kb-out { from { transform: scale(1.14) translate(2%,2%); } to { transform: scale(1); } }
        @keyframes dot { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
      `}</style>

      {/* Brand strip */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 4px 9px' }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.32em', color: P.blue }}>111 LONG LANE · SE1</div>
          {/* The page's only h1. It was a div, which left the whole site with
              no heading at all — the biggest structural SEO gap we had. */}
          <h1 style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 21, lineHeight: 1, letterSpacing: '-0.03em', margin: '3px 0 0' }}>
            The Old School <span style={{ color: P.pink }}>Yard</span>
          </h1>
        </div>
        {!PRERENDER && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 9px', border: `1px solid ${hours.open ? '#4ade80' : 'rgba(255,255,255,0.2)'}`, borderRadius: 100, fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.14em', color: hours.open ? '#4ade80' : 'rgba(234,242,255,0.6)' }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: hours.open ? '#4ade80' : '#ef4444', animation: 'dot 2s infinite' }} />
            {hours.open ? 'OPEN' : 'CLOSED'}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 6 }}><RotatingBanner accent={P.pink} /></div>
      <TonightStrip onOpen={onOpenKaraoke} />

      {/* 8-tile bento */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>

        {/* 1 · KARAOKE ROOMS — hero */}
        <button onClick={onOpenKaraoke} style={{ ...tile, gridColumn: 'span 2', aspectRatio: '2 / 1.24' }}>
          <PhotoCycle srcs={TOSY_SETS.stage} tone={0.5} interval={5200} lyric="Take me to the place where you go…">
            <div style={{ position: 'absolute', inset: 0, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Label c={P.pink}>★ KARAOKE ROOMS</Label>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.16em', color: 'rgba(234,242,255,0.75)' }}>5 ROOMS</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <Title size={30}>Your own<br/>private stage.</Title>
                  <Meta>FROM £7.50 PP · SESSIONS FROM 1 HR</Meta>
                </div>
                <MusicWaveform color={P.pink} />
              </div>
            </div>
          </PhotoCycle>
        </button>

        {/* 2 · BOOK NOW */}
        <button onClick={onOpenBooking} style={{
          ...tile, aspectRatio: '1/1', background: P.pink, border: 'none',
          padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', color: '#fff',
        }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 8.5, letterSpacing: '0.26em', color: 'rgba(255,255,255,0.85)' }}>RESERVE</div>
          <div>
            <div style={{ fontFamily: 'var(--display)', fontWeight: 800, fontSize: 25, lineHeight: 0.95, letterSpacing: '-0.03em' }}>Book<br/>now</div>
            <div style={{ marginTop: 9, width: 30, height: 30, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="12" height="12" viewBox="0 0 16 16"><path d="M3 8h10m-4-4l4 4-4 4" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round"/></svg>
            </div>
          </div>
        </button>

        {/* 3 · MENUS */}
        <button onClick={onOpenMenu} style={{ ...tile, aspectRatio: '1/1' }}>
          <PhotoCycle srcs={TOSY_SETS.cocktails} tone={0.5} interval={6000}>
            <div style={{ position: 'absolute', inset: 0, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Label c={P.yellow}>MENUS</Label>
              <div>
                <Title size={19}>Cocktails<br/>&amp; snacks</Title>
                <Meta>2 FOR £10 AT HAPPY HOUR →</Meta>
              </div>
            </div>
          </PhotoCycle>
        </button>

        {/* 4 · EVENT — row 3, full width. Auto-hides when no upcoming event */}
        <EventStrip onOpen={onOpenEvent} style={{ gridColumn: 'span 2' }} />

        {/* 5 · TONIGHT'S OFFERS — full-tile scrolling neon marquee */}
        <button onClick={onOpenOffers} style={{
          ...tile, aspectRatio: '1/1',
          background: `linear-gradient(150deg, ${P.blue}2e, transparent 70%)`, border: `1px solid ${P.blue}55`,
        }}>
          <style>{`
            @keyframes offer-glow {
              0%, 100% { opacity: 1; text-shadow: 0 0 8px currentColor, 0 0 22px currentColor; }
              50% { opacity: 0.82; text-shadow: 0 0 4px currentColor, 0 0 10px currentColor; }
              54% { opacity: 0.55; }
              58% { opacity: 1; }
            }
          `}</style>
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 4, overflow: 'hidden' }}>
            {[
              { c: P.pink, dur: '9s', rev: false },
              { c: P.yellow, dur: '13s', rev: true },
              { c: P.blue, dur: '10.5s', rev: false },
            ].map((row, n) => (
              <div key={n} style={{ display: 'flex', whiteSpace: 'nowrap', animation: `marquee ${row.dur} linear infinite ${row.rev ? 'reverse' : ''}` }}>
                {[0, 1].map(k => (
                  <span key={k} style={{
                    fontFamily: 'var(--display)', fontWeight: 800, fontSize: 24, letterSpacing: '-0.02em',
                    color: row.c, paddingRight: 14, flexShrink: 0,
                    animation: `offer-glow ${3 + n}s ease-in-out infinite`,
                  }}>COCKTAILS 2 FOR £10 ✦&nbsp;</span>
                ))}
              </div>
            ))}
          </div>
          <div style={{ position: 'absolute', inset: 0, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', pointerEvents: 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Label c={P.blue}>OFFERS</Label>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 7, padding: '3px 6px', borderRadius: 100, background: P.yellow, color: P.ink, letterSpacing: '0.16em', fontWeight: 700 }}>DAILY</span>
            </div>
            <Meta>HAPPY HOUR UNTIL 7PM</Meta>
          </div>
        </button>

        {/* 5 · COCKTAIL CLASS */}
        <button onClick={onOpenClass} style={{ ...tile, aspectRatio: '1/1' }}>
          <PhotoCycle srcs={TOSY_SETS.masterclass} tone={0.5} interval={6800}>
            <div style={{ position: 'absolute', inset: 0, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Label c={P.yellow}>MASTERCLASS</Label>
              <div>
                <Title size={19}>Cocktail<br/>class</Title>
                <Meta>£50PP · 3–4 COCKTAILS + KARAOKE</Meta>
              </div>
            </div>
          </PhotoCycle>
        </button>

        {/* 6 · TERRACE */}
        <button onClick={onOpenTerrace} style={{ ...tile, aspectRatio: '1/1' }}>
          <PhotoCycle srcs={TOSY_SETS.terrace} tone={0.48} interval={7400}>
            <div style={{ position: 'absolute', inset: 0, padding: 14, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <Label c={P.blue}>TERRACE</Label>
              <div>
                <Title size={19}>Heated &amp;<br/>covered</Title>
                <Meta>OPEN YEAR-ROUND</Meta>
              </div>
            </div>
          </PhotoCycle>
        </button>

        {/* 7 · FIND US — cartoon doodle map: both tubes, routes, TOSY pin */}
        <button onClick={onOpenFind} style={{
          ...tile, aspectRatio: '1/1', padding: 14,
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <Label c={P.blue}>FIND US</Label>
          <svg viewBox="0 0 120 100" style={{ width: '100%', flex: 1, margin: '4px 0' }}>
            {/* Thames + London Bridge road, top */}
            <rect x="0" y="0" width="120" height="9" fill="rgba(46,155,255,0.28)" />
            <path d="M0 4 Q10 6 20 4 T40 4 T60 4 T80 4 T100 4 T120 4" stroke="rgba(46,155,255,0.5)" strokeWidth="1" fill="none" />
            <path d="M30 0 L38 20" stroke="rgba(234,242,255,0.25)" strokeWidth="4" strokeLinecap="round" />
            {/* St Thomas St */}
            <path d="M24 34 L116 30" stroke="rgba(234,242,255,0.25)" strokeWidth="3.5" strokeLinecap="round" />
            <text x="86" y="27" fontFamily="var(--mono)" fontSize="4.6" letterSpacing="0.08em" fill="rgba(234,242,255,0.45)">ST THOMAS ST</text>
            {/* Borough High St (A3), left */}
            <path d="M20 12 Q14 50 10 96" stroke="rgba(234,242,255,0.25)" strokeWidth="4.5" strokeLinecap="round" />
            {/* the Shard */}
            <polygon points="66,30 71,4 76,30" fill="rgba(234,242,255,0.4)" />
            {/* London Bridge station */}
            <g>
              <circle cx="42" cy="21" r="5.5" fill="none" stroke="#ee2e24" strokeWidth="2.2" />
              <rect x="34" y="19.6" width="16" height="2.8" rx="1.4" fill={P.blue} />
              <text x="53" y="18" fontFamily="var(--mono)" fontSize="5.2" letterSpacing="0.06em" fill="#fff">LONDON</text>
              <text x="53" y="24.5" fontFamily="var(--mono)" fontSize="5.2" letterSpacing="0.06em" fill="#fff">BRIDGE</text>
            </g>
            {/* Guy's Hospital */}
            <text x="34" y="48" fontFamily="var(--mono)" fontSize="4.8" letterSpacing="0.06em" fill="rgba(234,242,255,0.55)">GUY'S</text>
            <text x="34" y="54" fontFamily="var(--mono)" fontSize="4.8" letterSpacing="0.06em" fill="rgba(234,242,255,0.55)">HOSPITAL</text>
            <circle cx="60" cy="50" r="4.5" fill="none" stroke="rgba(234,242,255,0.5)" strokeWidth="1" />
            <text x="60" y="52.2" textAnchor="middle" fontFamily="var(--sans)" fontWeight="700" fontSize="6" fill="rgba(234,242,255,0.6)">H</text>
            {/* Great Maze Lane / Crosby Row, vertical */}
            <path d="M74 32 Q72 55 70 76" stroke="rgba(234,242,255,0.25)" strokeWidth="3.5" strokeLinecap="round" />
            <text x="79" y="42" fontFamily="var(--mono)" fontSize="4.4" letterSpacing="0.06em" fill="rgba(234,242,255,0.4)" transform="rotate(84 79 42)">CROSBY ROW</text>
            {/* Long Lane */}
            <path d="M10 88 Q60 80 116 74" stroke="rgba(234,242,255,0.3)" strokeWidth="5" strokeLinecap="round" />
            <text x="36" y="82" fontFamily="var(--mono)" fontSize="5.4" letterSpacing="0.14em" fill="rgba(234,242,255,0.6)" transform="rotate(-5 36 82)">LONG LANE</text>
            {/* walking routes */}
            <path d="M44 27 Q76 34 74 36" stroke={P.blue} strokeWidth="1.5" fill="none" strokeDasharray="2.6 2.2" opacity="0.9" />
            <path d="M74 36 Q72 56 70 73" stroke={P.blue} strokeWidth="1.5" fill="none" strokeDasharray="2.6 2.2" opacity="0.9" />
            <path d="M20 86 Q45 82 63 79" stroke={P.pink} strokeWidth="1.5" fill="none" strokeDasharray="2.6 2.2" opacity="0.9" />
            {/* Borough station */}
            <g>
              <circle cx="13" cy="88" r="5.5" fill="none" stroke="#ee2e24" strokeWidth="2.2" />
              <rect x="5" y="86.6" width="16" height="2.8" rx="1.4" fill={P.blue} />
              <text x="24" y="97" fontFamily="var(--mono)" fontSize="5.2" letterSpacing="0.06em" fill="#fff">BOROUGH</text>
            </g>
            {/* TOSY — corner of Long Lane & Crosby Row */}
            <circle cx="70" cy="77" r="8.5" fill={P.pink} opacity="0.2" />
            <circle cx="70" cy="77" r="4.2" fill={P.pink} />
            <text x="82" y="70" textAnchor="middle" fontFamily="var(--mono)" fontSize="6" letterSpacing="0.12em" fill={P.yellow}>TOSY</text>
          </svg>
          <div>
            <Title size={17}>5 min from<br/>the tube</Title>
            <Meta>LONDON BRIDGE · BOROUGH →</Meta>
          </div>
        </button>

        {/* 8 · FAQs */}
        <button onClick={onOpenFAQ} style={{
          ...tile, aspectRatio: '1/1', padding: 14,
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        }}>
          <Label c={P.blue}>FAQs</Label>
          <div style={{ fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.15, color: 'rgba(234,242,255,0.9)', fontStyle: 'italic' }}>
            “Do I need<br/>to book?”
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 8, color: P.pink, letterSpacing: '0.18em' }}>12 ANSWERS →</div>
        </button>
      </div>

      {/* Hours strip — today-specific, so left out of the prerender */}
      {!PRERENDER && (
        <div style={{
          marginTop: 7, padding: '9px 12px', borderRadius: 100,
          background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)',
          display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden',
        }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: hours.open ? '#4ade80' : '#ef4444', boxShadow: hours.open ? '0 0 8px #4ade80' : 'none', flexShrink: 0, animation: hours.open ? 'dot 2s infinite' : 'none' }} />
          <span style={{ fontFamily: 'var(--sans)', fontSize: 11, color: 'rgba(234,242,255,0.92)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
            {hours.label}
          </span>
        </div>
      )}

      {/* Social proof */}
      <div style={{ marginTop: 8, display: 'flex', justifyContent: 'center', gap: 16, fontFamily: 'var(--mono)', fontSize: 8.5, color: 'rgba(234,242,255,0.55)', letterSpacing: '0.14em' }}>
        <span><span style={{ color: P.yellow }}>★</span> 4.2 GOOGLE</span>
        <span style={{ opacity: 0.3 }}>|</span>
        <span><span style={{ color: P.yellow }}>★</span> 5.0 DESIGNMYNIGHT</span>
      </div>

      {/* Fixed quickbar */}
      <div style={{
        position: 'fixed', bottom: 12, left: 10, right: 10, height: 62,
        borderRadius: 100, padding: '0 6px',
        background: 'rgba(10,18,38,0.7)',
        backdropFilter: 'blur(22px) saturate(180%)', WebkitBackdropFilter: 'blur(22px) saturate(180%)',
        border: '1px solid rgba(255,255,255,0.14)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-around',
        boxShadow: '0 10px 40px rgba(0,0,0,0.5)', zIndex: 50,
        maxWidth: 470, margin: '0 auto',
      }}>
        {[
          { label: 'Call', href: 'tel:02073576281', icon: <path d="M4 3h3l1.5 4-2 1.5a11 11 0 005 5L13 11.5 17 13v3a1 1 0 01-1.1 1A14 14 0 013 4.1 1 1 0 014 3z" /> },
          { label: 'WhatsApp', href: 'https://wa.me/447405289375', icon: <path d="M10 2a8 8 0 00-6.9 12L2 18l4.2-1.1A8 8 0 1010 2zm0 2a6 6 0 11-3.2 11.1l-.3-.2-2 .5.5-2-.2-.3A6 6 0 0110 4z" /> },
          { label: 'Book', primary: true, icon: <path d="M10 1l2.4 5.3 5.6.7-4.2 4 1.1 5.6L10 13.9 5.1 16.6l1.1-5.6-4.2-4 5.6-.7z" /> },
          { label: 'Maps', href: 'https://maps.google.com/?q=The+Old+School+Yard,+111+Long+Lane,+London+SE1+4PH', icon: <path d="M10 1a6 6 0 00-6 6c0 4.4 6 12 6 12s6-7.6 6-12a6 6 0 00-6-6zm0 8.2A2.2 2.2 0 1110 4.8a2.2 2.2 0 010 4.4z" /> },
          { label: 'Instagram', href: 'https://instagram.com/oldschoolyard', icon: <path d="M6 2h8a4 4 0 014 4v8a4 4 0 01-4 4H6a4 4 0 01-4-4V6a4 4 0 014-4zm4 4.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zm0 2a1.5 1.5 0 110 3 1.5 1.5 0 010-3zM14.8 5a.9.9 0 100 1.8.9.9 0 000-1.8z" /> },
        ].map((b, i) => (
          <a key={i} href={b.href} target={b.href && b.href.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
            onClick={b.primary ? (e) => { e.preventDefault(); onOpenBooking(); } : undefined} style={{
            textDecoration: 'none',
            flex: 1, height: 48, borderRadius: 100, border: 'none', cursor: 'pointer',
            background: b.primary ? BRAND.pink : 'transparent',
            color: b.primary ? '#fff' : 'rgba(234,242,255,0.85)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
          }}>
            <svg width={b.primary ? 17 : 15} height={b.primary ? 17 : 15} viewBox="0 0 20 20" fill="currentColor">{b.icon}</svg>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 7.5, letterSpacing: '0.12em', fontWeight: b.primary ? 700 : 500 }}>{b.label.toUpperCase()}</span>
          </a>
        ))}
      </div>
    </main>
  );
}

window.BentoViewV2 = BentoViewV2;
window.TOSY_BRAND = BRAND;
